import { View, Pressable, Box, Spinner, VStack } from "@gluestack-ui/themed";
import { CameraProps } from "@repo/core/components/core/Camera/types";
import React from "react";
import { useWindowDimensions } from "react-native";

import { CameraController } from "@repo/core/components/core/Camera/controller";
import { Text } from "@repo/core/components/core/Text";

export const Camera: React.FC<CameraProps> = ({
  facing,
  onBarcodeScanned,
  onImageCaptured,
  onCancelCapture,
  onToggleCameraFacing,
  shutterPlacement = "bottom-center"
}) => {
  const { width, height } = useWindowDimensions();
  const shouldUseBottomControls =
    shutterPlacement === "right-center" && width < 768 && width < height;
  const effectiveShutterPlacement = shouldUseBottomControls
    ? "bottom-center"
    : shutterPlacement;
  const {
    hasPermission,
    videoRef,
    isCameraReady,
    setIsCameraReady,
    captureImage
  } = CameraController({
    facing,
    onImageCaptured,
    onBarcodeScanned
  });
  if (hasPermission === null) {
    return (
      <VStack flex={1} alignItems="center" justifyContent="center" gap="$3">
        <Spinner color="$white" size="large" />
        <Text color="$white" fontSize="$md" fontWeight={600}>
          Carregando câmera...
        </Text>
      </VStack>
    );
  }

  if (hasPermission === false) {
    return (
      <View flex={1} alignItems="center" justifyContent="center">
        <Text fontSize="$xl" fontWeight={500}>
          Permissão para acessar a câmera foi negada.
        </Text>
      </View>
    );
  }

  return (
    <View flex={1}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        onCanPlay={() => setIsCameraReady(true)}
        onLoadedMetadata={() => setIsCameraReady(true)}
        onWaiting={() => setIsCameraReady(false)}
        style={{
          flex: 1,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: facing === "front" ? "scaleX(-1)" : undefined
        }}
      />
      {!isCameraReady && (
        <VStack
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          alignItems="center"
          justifyContent="center"
          gap="$3"
          bg="rgba(0, 0, 0, 0.72)"
          style={{ zIndex: 90 }}
        >
          <Spinner color="$white" size="large" />
          <Text color="$white" fontSize="$md" fontWeight={600}>
            Carregando câmera...
          </Text>
        </VStack>
      )}
      {onImageCaptured && (
        <View
          position="absolute"
          bottom={effectiveShutterPlacement === "right-center" ? "$0" : "$6"}
          right={
            effectiveShutterPlacement === "right-center" ? "$6" : undefined
          }
          top={effectiveShutterPlacement === "right-center" ? 0 : undefined}
          alignSelf={
            effectiveShutterPlacement === "right-center" ? undefined : "center"
          }
          justifyContent={
            effectiveShutterPlacement === "right-center" ? "center" : undefined
          }
          flexDirection={
            effectiveShutterPlacement === "bottom-center" ? "row" : undefined
          }
          alignItems={
            effectiveShutterPlacement === "bottom-center" ? "center" : undefined
          }
          gap={effectiveShutterPlacement === "bottom-center" ? "$5" : undefined}
          style={{ zIndex: 100 }}
        >
          {effectiveShutterPlacement === "bottom-center" &&
            !onCancelCapture && <Box w={64} h={76} style={{ opacity: 0 }} />}
          {effectiveShutterPlacement === "bottom-center" && onCancelCapture && (
            <Pressable
              onPress={onCancelCapture}
              accessibilityLabel="Cancelar captura"
              cursor="pointer"
              _pressed={{ opacity: 0.88 }}
              w={64}
              h={76}
              alignItems="center"
              justifyContent="center"
            >
              <Text color="$white" fontSize="$3xl" fontWeight={700}>
                ×
              </Text>
            </Pressable>
          )}
          <Pressable
            onPress={captureImage}
            accessibilityLabel="Capture photo"
            cursor="pointer"
            _pressed={{ opacity: 0.88 }}
            w={76}
            h={76}
            borderRadius={38}
            borderWidth={4}
            borderColor="$white"
            bg="rgba(0, 0, 0, 0.25)"
            alignItems="center"
            justifyContent="center"
          >
            <Box w={62} h={62} borderRadius={31} bg="$white" />
          </Pressable>
          {effectiveShutterPlacement === "bottom-center" &&
            onToggleCameraFacing && (
              <Pressable
                onPress={onToggleCameraFacing}
                accessibilityLabel="Alternar câmera"
                cursor="pointer"
                _pressed={{ opacity: 0.88 }}
                w={64}
                h={76}
                alignItems="center"
                justifyContent="center"
              >
                <Text color="$white" fontSize="$3xl" fontWeight={700}>
                  ↻
                </Text>
              </Pressable>
            )}
          {effectiveShutterPlacement === "bottom-center" &&
            !onToggleCameraFacing && (
              <Box w={64} h={76} style={{ opacity: 0 }} />
            )}
          {effectiveShutterPlacement === "right-center" && onCancelCapture && (
            <Pressable
              onPress={onCancelCapture}
              accessibilityLabel="Cancelar captura"
              cursor="pointer"
              _pressed={{ opacity: 0.88 }}
              position="absolute"
              top="50%"
              w={56}
              h={56}
              alignItems="center"
              justifyContent="center"
              style={{ transform: "translateY(74px)" }}
            >
              <Text color="$white" fontSize="$3xl" fontWeight={700}>
                ×
              </Text>
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
};
